import { useEffect } from 'react'
import { useBlocker } from 'react-router-dom'
import { Button, Group, Modal, Stack, Text } from '@mantine/core'

/**
 * Stops a navigation away from unsaved edits.
 *
 * Two different exits have to be covered and only one of them is ours. A click
 * on a link is a router navigation, which useBlocker can intercept and hold
 * until the reader decides — so that one gets a real dialog naming what would
 * be lost. Closing the tab or reloading is the browser's, and all a page may do
 * there is set the beforeunload flag; the wording of that prompt belongs to the
 * browser and cannot be chosen.
 *
 * Shared by both apps rather than copied into each. The proceed-on-clean effect
 * below is the subtle part — without it, saving while the dialog is up leaves
 * the dialog asking about changes that no longer exist — and that is exactly the
 * kind of detail that drifts when it lives in two places.
 *
 * Deliberately keyed on pathname alone. Changing a query string is the same
 * page, and stopping someone from doing that would be an interruption with
 * nothing behind it.
 */
export default function UnsavedChangesGuard({
  when,
  message = 'Your changes have not been saved. Leaving this page will discard them.',
}: {
  when: boolean
  message?: string
}) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    if (!when) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [when])

  // A save or a revert while the dialog is up settles the question, so let the
  // navigation through rather than asking about changes that no longer exist.
  useEffect(() => {
    if (blocker.state === 'blocked' && !when) blocker.proceed()
  }, [blocker, when])

  return (
    <Modal
      opened={blocker.state === 'blocked'}
      onClose={() => blocker.reset?.()}
      title="Leave without saving?"
    >
      <Stack gap="md">
        <Text size="sm">{message}</Text>
        <Group justify="flex-end">
          {/* Staying is the safe option, so it is the default one. */}
          <Button variant="default" onClick={() => blocker.reset?.()}>
            Stay on this page
          </Button>
          <Button color="red" onClick={() => blocker.proceed?.()}>
            Discard changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
